import type { JSX } from 'react';
import { Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { REFEREE_RELATIONSHIPS } from '@/shared/constants/referee-relationships';
import { REQUEST_STATUS_CONFIG } from '@/features/verifications/types/verification.types';
import type { Verification } from '@/features/verifications/types/verification.types';

interface ReferenceContactCardProps {
  verification: Verification;
  onDelete: (id: string) => void;
  onSend: (id: string) => void;
  isSending: boolean;
}

function subtitleText(verification: Verification): string | null {
  if (verification.refereeJobTitle) return verification.refereeJobTitle;
  const match = REFEREE_RELATIONSHIPS.find((rel) => rel.value === verification.refereeRelationship);
  return match?.label ?? null;
}

export function ReferenceContactCard({
  verification,
  onDelete,
  onSend,
  isSending,
}: ReferenceContactCardProps): JSX.Element {
  const status = REQUEST_STATUS_CONFIG[verification.requestStatus];
  const subtitle = subtitleText(verification);
  const isDraft = verification.requestStatus === 'DRAFT';

  return (
    <li className="flex flex-col gap-2 rounded-lg border bg-background p-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="break-words text-base font-semibold">{verification.refereeName}</h3>
          <Badge variant={status.color}>{status.label}</Badge>
        </div>
        {subtitle && <p className="break-words text-sm text-muted-foreground">{subtitle}</p>}
        <p className="break-all text-sm text-muted-foreground">{verification.refereeEmail}</p>
      </div>
      {isDraft && (
        <div className="flex shrink-0 items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            disabled={isSending}
            onClick={(): void => onSend(verification.id)}
          >
            Send request
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Delete contact ${verification.refereeName}`}
            onClick={(): void => onDelete(verification.id)}
          >
            <Trash2 aria-hidden="true" className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      )}
    </li>
  );
}
